'use client'

import { faAngleDown, faXmark } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { Slot } from '@radix-ui/react-slot'
import * as React from 'react'

import { cn } from '@/lib/utils'

function Menu(props: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot="menu" {...props} />
}

function MenuTrigger(props: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="menu-trigger" {...props} />
}

function MenuClose(props: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="menu-close" {...props} />
}

type MenuContentProps = React.ComponentProps<typeof DialogPrimitive.Content> & {
  title?: React.ReactNode
  showClose?: boolean
  closeLabel?: string
  /**
   * Densidade das linhas (`--menu-item-small/medium/large` do DS): o `.br-menu`
   * serve os três valores e escolhe um em `--menu-item-padding`.
   */
  density?: 'small' | 'medium' | 'large'
}

// Bloco de tokens do .br-menu. Fica no scrim e no painel porque, no Radix, eles
// são irmãos dentro do Portal — não há ancestral comum onde declarar.
const menuTokens = [
  '[--menu-zindex:var(--z-index-layer-3)]',
  '[--menu-background-submenu:var(--gray-2)]',
  '[--menu-logo:40px]',
  '[--menu-item-small:var(--spacing-scale-base)]',
  '[--menu-item-medium:var(--spacing-scale-2x)]',
  '[--menu-item-large:var(--spacing-scale-3x)]',
].join(' ')

const densityToken: Record<'small' | 'medium' | 'large', string> = {
  small: '[--menu-item-padding:var(--menu-item-small)]',
  medium: '[--menu-item-padding:var(--menu-item-medium)]',
  large: '[--menu-item-padding:var(--menu-item-large)]',
}

function MenuContent({
  className,
  children,
  title = 'Menu',
  showClose = true,
  closeLabel = 'Fechar menu',
  density = 'medium',
  ...props
}: MenuContentProps) {
  return (
    <DialogPrimitive.Portal data-slot="menu-portal">
      <DialogPrimitive.Overlay
        data-slot="menu-scrim"
        className={cn(
          menuTokens,
          'fixed inset-0 z-[var(--menu-zindex)]',
          'bg-[var(--surface-overlay-scrim)]',
          'data-[state=closed]:animate-out data-[state=open]:animate-in',
          'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0'
        )}
      />
      <DialogPrimitive.Content
        data-slot="menu-content"
        // .menu-panel: overlay de tela cheia do DS. No core.css o `.menu-container`
        // é `position:fixed; inset:0` e o `.menu-panel` é `flex:1; height:100vh`
        // (sem largura própria → ocupa a viewport toda); o scrim fica atrás
        // (`position:absolute`). Coluna flex, rolável, fundo do DS. Foco preso e
        // Escape vêm do Radix Dialog. (As variantes com largura própria — `push`/
        // `contextual` — não fazem parte desta geração.)
        className={cn(
          menuTokens,
          densityToken[density],
          'fixed inset-0 z-[calc(var(--menu-zindex)+1)] flex h-screen w-full flex-col',
          '[--focus-offset:calc(var(--spacing-scale-half)*-1)]',
          'bg-[var(--background)]',
          'data-[state=closed]:animate-out data-[state=open]:animate-in',
          'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          className
        )}
        {...props}
      >
        <MenuHeader>
          <DialogPrimitive.Title
            data-slot="menu-title"
            // A logo no título respeita --menu-logo (40px), como no DS.
            className="flex flex-1 items-center [font-weight:var(--font-weight-semi-bold)] [&_img]:max-h-[var(--menu-logo)]"
          >
            {title}
          </DialogPrimitive.Title>
          {showClose && (
            <DialogPrimitive.Close
              aria-label={closeLabel}
              className={cn(
                'inline-flex size-[var(--spacing-scale-4x)] items-center justify-center',
                'cursor-pointer rounded-full border-0 bg-transparent text-[var(--interactive)]',
                'transition-[background-image] [transition-duration:var(--duration-fast)]',
                'hover:[background-image:linear-gradient(rgba(var(--interactive-rgb),var(--hover)),rgba(var(--interactive-rgb),var(--hover)))]',
                'outline-none focus-visible:outline focus-visible:[outline-style:var(--focus-style)]',
                'focus-visible:[outline-width:var(--focus-width)] focus-visible:[outline-offset:var(--focus-offset)]',
                'focus-visible:[outline-color:var(--focus-color)]',
                '[&_.svg-inline--fa]:pointer-events-none'
              )}
            >
              <FontAwesomeIcon icon={faXmark} aria-hidden="true" />
            </DialogPrimitive.Close>
          )}
        </MenuHeader>
        {children}
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  )
}

// .menu-header: borda inferior + padding base.
function MenuHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="menu-header"
      className={cn(
        'flex items-center gap-[var(--spacing-scale-base)]',
        'border-b border-[var(--border-color)]',
        'px-[var(--spacing-scale-base)] py-[var(--spacing-scale-base)]',
        className
      )}
      {...props}
    />
  )
}

// .menu-body: área rolável com a navegação (nav > ul).
function MenuBody({ className, children, ...props }: React.ComponentProps<'nav'>) {
  return (
    <nav data-slot="menu-body" className={cn('flex-1 overflow-auto', className)} {...props}>
      <ul className="m-0 list-none p-0">{children}</ul>
    </nav>
  )
}

type MenuItemProps = React.ComponentProps<'a'> & {
  asChild?: boolean
  // Ícone/marca à esquerda (decorativo — passe aria-hidden no seu ícone).
  icon?: React.ReactNode
  // Slot de apoio à direita (badge, seta etc.).
  support?: React.ReactNode
  /** Linha corrente (`a.menu-item.active` do DS): fundo --active e tinta clara. */
  active?: boolean
  /** Linha desabilitada: --disabled e sem eventos, como o `.menu-item:disabled`. */
  disabled?: boolean
}

// .menu-item: linha com ícone + conteúdo (+ apoio). Composável via asChild
// (por padrão um <a>). Sempre dentro de um <li>.
function MenuItem({
  className,
  asChild = false,
  icon,
  support,
  active = false,
  disabled = false,
  children,
  ...props
}: MenuItemProps) {
  const Comp = asChild ? Slot : 'a'
  return (
    <li className="m-0">
      <Comp
        data-slot="menu-item"
        data-active={active || undefined}
        aria-disabled={disabled || undefined}
        aria-current={active ? 'page' : undefined}
        className={cn(
          'flex w-full items-center bg-transparent text-left',
          // O padding da linha vem de --menu-item-padding (a densidade do DS).
          'p-[var(--menu-item-padding)]',
          'text-[color:var(--interactive)] no-underline',
          'transition-[background-image] [transition-duration:var(--duration-fast)]',
          'hover:[background-image:linear-gradient(rgba(var(--interactive-rgb),var(--hover)),rgba(var(--interactive-rgb),var(--hover)))]',
          'outline-none focus-visible:outline focus-visible:[outline-style:var(--focus-style)]',
          'focus-visible:[outline-width:var(--focus-width)] focus-visible:[outline-offset:var(--focus-offset)]',
          'focus-visible:[outline-color:var(--focus-color)]',
          // Linha corrente: o DS troca a tinta interativa para a clara e pinta o
          // fundo com --active; o foco também vai para o dourado do fundo escuro.
          active && [
            'bg-[var(--active)]',
            '[--interactive-rgb:var(--color-dark-rgb)] [--interactive:var(--color-dark)]',
            'focus-visible:[outline-color:var(--focus-color-dark)]',
          ],
          // Desabilitada: --disabled e nada de eventos nos filhos.
          'aria-disabled:cursor-not-allowed aria-disabled:[opacity:var(--disabled)]',
          'aria-disabled:[&_*]:pointer-events-none',
          'disabled:cursor-not-allowed disabled:[opacity:var(--disabled)]',
          className
        )}
        {...props}
      >
        {icon != null && (
          <span
            data-slot="menu-item-icon"
            className="mr-[var(--spacing-scale-2x)] inline-flex shrink-0"
          >
            {icon}
          </span>
        )}
        <span data-slot="menu-item-content" className="flex-1">
          {children}
        </span>
        {support != null && (
          <span
            data-slot="menu-item-support"
            className="ml-[var(--spacing-scale-2x)] inline-flex shrink-0"
          >
            {support}
          </span>
        )}
      </Comp>
    </li>
  )
}

type MenuFolderProps = Omit<React.ComponentProps<'details'>, 'title'> & {
  title: React.ReactNode
  icon?: React.ReactNode
}

// .drop-menu: pasta com submenu colapsável. <details>/<summary> nativos → sem JS,
// acessível por teclado; a seta gira ao abrir. Submenu com fundo alternativo.
function MenuFolder({ className, title, icon, children, ...props }: MenuFolderProps) {
  return (
    <li className="m-0">
      <details
        data-slot="menu-folder"
        className={cn('group border-b border-[var(--border-color)]', className)}
        {...props}
      >
        <summary
          data-slot="menu-folder-trigger"
          className={cn(
            'flex cursor-pointer list-none items-center',
            'p-[var(--menu-item-padding)]',
            'text-[color:var(--interactive)]',
            'outline-none focus-visible:outline focus-visible:[outline-style:var(--focus-style)]',
            'focus-visible:[outline-width:var(--focus-width)] focus-visible:[outline-offset:calc(var(--spacing-scale-half)*-1)]',
            'focus-visible:[outline-color:var(--focus-color)]',
            '[&::-webkit-details-marker]:hidden'
          )}
        >
          {icon != null && (
            <span className="mr-[var(--spacing-scale-2x)] inline-flex shrink-0">{icon}</span>
          )}
          <span className="flex-1">{title}</span>
          <FontAwesomeIcon
            icon={faAngleDown}
            aria-hidden="true"
            className="pointer-events-none ml-[var(--spacing-scale-2x)] shrink-0 transition-transform [transition-duration:var(--duration-fast)] group-open:rotate-180"
          />
        </summary>
        <ul
          data-slot="menu-folder-list"
          // Submenu com o fundo alternativo do DS (--menu-background-submenu).
          className="m-0 list-none bg-[var(--menu-background-submenu)] p-0 [&_[data-slot=menu-item]]:pl-[var(--spacing-scale-5x)]"
        >
          {children}
        </ul>
      </details>
    </li>
  )
}

// .menu-footer: seções separadas por borda (logos, links, social, info).
function MenuFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="menu-footer"
      className={cn(
        '[&>*:not(:last-child)]:border-b [&>*:not(:last-child)]:border-[var(--border-color)]',
        className
      )}
      {...props}
    />
  )
}

export {
  Menu,
  MenuBody,
  MenuClose,
  MenuContent,
  MenuFolder,
  MenuFooter,
  MenuHeader,
  MenuItem,
  MenuTrigger,
}
